<template>
  <div class="tools-grid" :class="[`size-${size}`, `columns-${columns}`]">
    <div v-for="tool in tools" :key="tool.id" class="tool-item">
      <a
        :href="tool.url"
        target="_blank"
        rel="noopener noreferrer"
        class="tool-link"
        @click="handleClick(tool)"
      >
        <div class="icon-container">
          <img v-if="tool.logo" :src="tool.logo" :alt="tool.label" class="tool-logo" />
          <IonIcon v-else :name="tool.icon" class="icon-tool"></IonIcon>
        </div>
        <span class="tool-label">{{ tool.label }}</span>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { IonIcon } from '@ionic/vue';
import { useLocale } from '@/common/composables/useLocale';
import microsoft_365 from '@/assets/icons/microsoft_365.svg';
import logo_upward from '@/assets/icons/logo_upward_2.svg';
import logo_one_drive from '@/assets/images/Microsoft_OneDrive_Icon_(2025_-_present).svg.png';

interface Tool {
  id: string;
  url: string;
  icon?: string;
  logo?: string;
  label: string;
}

interface Props {
  size?: 'compact' | 'normal';
  columns?: 4 | 3 | 2;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'normal',
  columns: 4,
});

const emit = defineEmits<{
  (e: 'tool-click', tool: Tool): void;
}>();

const { t } = useLocale();

// Tool definitions
const tools = computed<Tool[]>(() => [
  {
    id: 'upward',
    url: 'https://upward.em-normandie.fr/',
    logo: logo_upward,
    label: t('common.tools.upward'),
  },
  {
    id: 'microsoft365',
    url: 'https://office365.com/',
    logo: microsoft_365,
    label: t('common.tools.microsoft365'),
  },
  {
    id: 'virtualMachine',
    url: 'https://windows.cloud.microsoft/?navFrom=avdweb#/devices',
    icon: 'laptop-outline',
    label: t('common.tools.virtualMachine'),
  },
  {
    id: 'documents',
    url: 'https://emnormandie1-my.sharepoint.com/',
    logo: logo_one_drive,
    label: t('common.tools.documents'),
  },
  {
    id: 'learningCenter',
    url: 'https://lc.em-normandie.fr/',
    icon: 'library-outline',
    label: t('common.tools.learningCenter'),
  },
  {
    id: 'housing',
    url: 'https://em-normandie.studapart.com/fr/',
    icon: 'business-outline',
    label: t('common.tools.housing'),
  },
  {
    id: 'warning',
    url: 'https://em-normandie.signalement.net/',
    icon: 'warning-outline',
    label: t('common.tools.warning'),
  },
  {
    id: 'bugReport',
    url: 'https://forms.office.com/e/kLBGrbHkq4?origin=lprLink',
    icon: 'bug-outline',
    label: t('common.tools.bugReport'),
  },
]);

function handleClick(tool: Tool) {
  emit('tool-click', tool);
}
</script>

<style scoped>
.tools-grid {
  display: grid;
  gap: 12px;
}

/* Columns variants */
.tools-grid.columns-4 {
  grid-template-columns: repeat(4, 1fr);
}

.tools-grid.columns-3 {
  grid-template-columns: repeat(3, 1fr);
}

.tools-grid.columns-2 {
  grid-template-columns: repeat(2, 1fr);
}

.tool-item {
  display: flex;
  align-items: center;
  justify-content: center;
}

.tool-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  padding: 12px 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  text-decoration: none;
  width: 100%;
  border: 1px solid transparent;
}

.tool-link:hover,
.tool-link:active {
  background: hsl(from var(--ion-color-primary) h s l / 0.1);
}

.icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.tool-logo {
  object-fit: contain;
}

.icon-tool {
  color: var(--ion-text-color);
}

.tool-label {
  font-weight: 500;
  color: var(--ion-text-color);
  text-align: center;
  line-height: 1.2;
  word-wrap: break-word;
  max-width: 100%;
}

/* Size: Normal (desktop popover) */
.tools-grid.size-normal {
  gap: 12px;
}

.tools-grid.size-normal .tool-link {
  min-height: 80px;
  padding: 12px 8px;
}

.tools-grid.size-normal .icon-container {
  width: 32px;
  height: 32px;
}

.tools-grid.size-normal .tool-logo {
  width: 28px;
  height: 28px;
}

.tools-grid.size-normal .icon-tool {
  font-size: 28px;
}

.tools-grid.size-normal .tool-label {
  font-size: 11px;
  min-height: 26px;
  display: flex;
  align-items: center;
}

/* Size: Compact (mobile drawer) */
.tools-grid.size-compact {
  gap: 8px;
}

.tools-grid.size-compact .tool-link {
  min-height: 60px;
  padding: 8px 4px;
  gap: 4px;
}

.tools-grid.size-compact .icon-container {
  width: 24px;
  height: 24px;
}

.tools-grid.size-compact .tool-logo {
  width: 24px;
  height: 24px;
}

.tools-grid.size-compact .icon-tool {
  font-size: 20px;
}

.tools-grid.size-compact .tool-label {
  font-size: 9px;
  line-height: 1.1;
}
</style>
